import { Alert, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DynamicIcon } from 'src/components';
import {
  PushNotificationSubscription,
  useDeactivatePushNotificationSubscription,
  usePushNotificationCapability,
  useUpsertPushNotificationSubscription,
} from 'src/graphql';

const PUSH_SUBSCRIPTION_CHANGE_MESSAGE = 'PUSH_SUBSCRIPTION_CHANGE';

export const PushNotificationSettings = () => {
  const [isLoading, setIsLoading] = useState(false);
  const isBrowser = typeof window !== 'undefined';
  const isServiceWorkerSupported = isBrowser && 'serviceWorker' in navigator;
  const isPushManagerSupported = isBrowser && 'PushManager' in window;
  const isNotificationSupported = isBrowser && 'Notification' in window;
  const isSupported =
    isServiceWorkerSupported && isPushManagerSupported && isNotificationSupported;

  const isIOS = isBrowser && /iphone|ipad|ipod/i.test(window.navigator.userAgent);
  const isStandalone = isBrowser
    ? window.matchMedia('(display-mode: standalone)').matches ||
      Boolean((window.navigator as unknown as { standalone?: boolean }).standalone)
    : false;
  const requiresInstallation = isIOS && !isStandalone;

  const swUrl = useMemo(
    () => (import.meta.env.DEV ? '/dev-sw.js?dev-sw' : '/service-worker.js'),
    [],
  );

  const {
    capability,
    loading: capabilityLoading,
    refetch,
  } = usePushNotificationCapability({
    skip: !isSupported || requiresInstallation,
  });
  const { upsertPushNotificationSubscription, loading: upserting } =
    useUpsertPushNotificationSubscription();
  const { deactivatePushNotificationSubscription, loading: deactivating } =
    useDeactivatePushNotificationSubscription();

  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [currentEndpoint, setCurrentEndpoint] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSupported || requiresInstallation) {
      return;
    }

    let cancelled = false;

    const registerServiceWorker = async () => {
      try {
        const existingRegistration = await navigator.serviceWorker.getRegistration();
        if (!existingRegistration) {
          await navigator.serviceWorker.register(swUrl);
        }
        const readyRegistration = await navigator.serviceWorker.ready;
        if (!cancelled) {
          setRegistration(readyRegistration);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError('Unable to register the service worker required for push notifications.');
        }
      }
    };

    void registerServiceWorker();

    return () => {
      cancelled = true;
    };
  }, [isSupported, requiresInstallation, swUrl]);

  useEffect(() => {
    if (!registration) {
      setCurrentEndpoint(null);
      return;
    }

    let cancelled = false;

    const resolveSubscription = async () => {
      try {
        const existingSubscription = await registration.pushManager.getSubscription();
        if (!cancelled) {
          setCurrentEndpoint(existingSubscription?.endpoint ?? null);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setCurrentEndpoint(null);
        }
      }
    };

    void resolveSubscription();

    return () => {
      cancelled = true;
    };
  }, [registration, capability?.subscriptions]);

  const subscribeAndSync = useCallback(
    async (options: { promptUser: boolean }) => {
      if (!isSupported) {
        throw new Error('Push notifications are not supported in this browser.');
      }

      if (!registration) {
        if (options.promptUser) {
          throw new Error(
            'Service worker registration is still initializing. Please try again.',
          );
        }
        return;
      }

      if (!capability) {
        throw new Error('Push notification capability is still loading. Please try again.');
      }

      if (!capability.vapidPublicKey) {
        throw new Error('Push notifications are not configured yet.');
      }

      if (!isNotificationSupported) {
        throw new Error('Notifications API is not available in this environment.');
      }

      if (Notification.permission === 'denied') {
        throw new Error('Browser notifications are blocked.');
      }

      if (Notification.permission === 'default') {
        if (!options.promptUser) {
          return;
        }

        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          throw new Error('Permission to show notifications was not granted.');
        }
      }

      const existingSubscription = await registration.pushManager.getSubscription();
      const activeSubscription =
        existingSubscription ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(
            capability.vapidPublicKey,
          ) as unknown as ArrayBuffer,
        }));

      const authKey = arrayBufferToBase64(activeSubscription.getKey('auth'));
      const p256dhKey = arrayBufferToBase64(activeSubscription.getKey('p256dh'));

      await upsertPushNotificationSubscription({
        endpoint: activeSubscription.endpoint,
        authKey,
        p256dhKey,
        expirationTime: activeSubscription.expirationTime
          ? new Date(activeSubscription.expirationTime).toISOString()
          : null,
        userAgent: isBrowser ? window.navigator.userAgent : null,
      });

      setCurrentEndpoint(activeSubscription.endpoint);
      await new Promise((res) => setTimeout(res, 5000));
      await refetch();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      capability?.vapidPublicKey,
      isBrowser,
      isNotificationSupported,
      isSupported,
      refetch,
      registration,
      upsertPushNotificationSubscription,
    ],
  );

  useEffect(() => {
    if (!isSupported || !('serviceWorker' in navigator)) return;

    const { serviceWorker } = navigator;

    type SWMessage = { type: string };

    const isSWMessage = (data: unknown): data is SWMessage =>
      typeof data === 'object' && data !== null && 'type' in data;

    const abort = new AbortController();

    const handleMessage = (event: MessageEvent) => {
      if (!isSWMessage(event.data)) return;

      if (event.data.type === PUSH_SUBSCRIPTION_CHANGE_MESSAGE) {
        void subscribeAndSync({ promptUser: false }).catch((err: unknown) => {
          setError(err instanceof Error ? err.message : String(err));
        });
      }
    };

    serviceWorker.addEventListener('message', handleMessage, { signal: abort.signal });

    return () => abort.abort();
  }, [isSupported, subscribeAndSync]);

  const activeSubscriptions = useMemo(() => {
    return capability?.subscriptions.filter((item) => item.isActive) ?? [];
  }, [capability?.subscriptions]);

  const isSubscribedOnThisDevice = useMemo(() => {
    return Boolean(
      currentEndpoint && activeSubscriptions.some((item) => item.endpoint === currentEndpoint),
    );
  }, [activeSubscriptions, currentEndpoint]);

  const otherActiveSubscriptions = useMemo(() => {
    return activeSubscriptions.filter((item) => item.endpoint !== currentEndpoint);
  }, [activeSubscriptions, currentEndpoint]);

  const permission = isNotificationSupported ? Notification.permission : undefined;
  const isBlocked = permission === 'denied';

  const isProcessing = isLoading || capabilityLoading || upserting || deactivating;

  const handleEnable = useCallback(async () => {
    setError(null);
    try {
      setIsLoading(true);
      await subscribeAndSync({ promptUser: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  }, [subscribeAndSync]);

  const handleDisable = useCallback(async () => {
    if (!registration) {
      return;
    }

    setError(null);

    try {
      const existingSubscription = await registration.pushManager.getSubscription();
      const endpointToDeactivate = existingSubscription?.endpoint ?? currentEndpoint;

      if (endpointToDeactivate) {
        await deactivatePushNotificationSubscription(endpointToDeactivate);
      }

      if (existingSubscription) {
        await existingSubscription.unsubscribe().catch(() => undefined);
      }

      setCurrentEndpoint(null);
      await refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [currentEndpoint, deactivatePushNotificationSubscription, refetch, registration]);

  const statusMessage = useMemo(() => {
    if (!isSupported) {
      return 'Push notifications are not supported by this browser.';
    }

    if (requiresInstallation) {
      return 'Install ProVision on your Home Screen to enable push notifications on this device.';
    }

    if (!capability?.vapidPublicKey) {
      return 'Push notifications are not yet configured for this environment.';
    }

    if (isBlocked) {
      return 'Push notifications are currently blocked. Update your browser settings to re-enable them.';
    }

    if (isSubscribedOnThisDevice) {
      return 'Push notifications are enabled on this device. You will receive updates even when ProVision is closed.';
    }

    return 'Enable push notifications to receive alerts on this device when you are away from ProVision.';
  }, [
    capability?.vapidPublicKey,
    isBlocked,
    isSubscribedOnThisDevice,
    isSupported,
    requiresInstallation,
  ]);

  const canEnable =
    isSupported &&
    !requiresInstallation &&
    !isSubscribedOnThisDevice &&
    !isBlocked &&
    Boolean(capability?.vapidPublicKey);

  const canDisable = isSubscribedOnThisDevice && !isProcessing;

  return (
    <Stack direction="row" gap={3} alignItems="flex-start">
      <DynamicIcon
        name="bell-ring"
        library="lucide"
        size={32}
        sx={{
          fontSize: '32px',
        }}
      />
      <Stack spacing={1} sx={{ flex: 1 }}>
        <Typography variant="body1">Push Notifications</Typography>
        <Typography
          variant="caption"
          fontSize={12}
          sx={{ color: (theme) => theme.palette.text.secondary }}
        >
          {statusMessage}
        </Typography>
        {otherActiveSubscriptions.length > 0 && (
          <Stack spacing={0.5}>
            <Typography
              variant="caption"
              fontSize={12}
              sx={{ color: (theme) => theme.palette.text.secondary }}
            >
              Also enabled on the following devices:
            </Typography>
            {otherActiveSubscriptions.map((item) => (
              <Typography
                key={item.id}
                variant="caption"
                fontSize={12}
                sx={{ color: (theme) => theme.palette.text.secondary }}
              >
                • {formatUserAgent(item)}
              </Typography>
            ))}
          </Stack>
        )}
        {!!requiresInstallation && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Install ProVision to your Home Screen from Safari&apos;s share menu, then reopen
            the app to enable push notifications on iOS.
          </Alert>
        )}
        {!!isBlocked && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Browser notifications are blocked. Update your site permissions in your browser or
            operating system settings to allow notifications from ProVision.
          </Alert>
        )}
        {!!error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={isSubscribedOnThisDevice}
                onChange={(_, checked) => (checked ? handleEnable() : handleDisable())}
                disabled={isSubscribedOnThisDevice ? !canDisable : !canEnable}
                inputProps={{ 'aria-label': 'toggle push notifications' }}
              />
            }
            label={
              isSubscribedOnThisDevice
                ? 'Enabled on this device'
                : 'Toggle on to enable notifications on this device'
            }
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  if (typeof window === 'undefined') {
    throw new Error('Push notifications are only available in the browser.');
  }

  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

function arrayBufferToBase64(buffer: ArrayBuffer | null): string {
  if (typeof window === 'undefined') {
    throw new Error('Push notifications are only available in the browser.');
  }

  if (!buffer) {
    throw new Error('Push subscription is missing required encryption keys.');
  }

  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

function formatUserAgent(subscription: PushNotificationSubscription): string {
  if (!subscription.userAgent) {
    return 'Unknown device';
  }
  return subscription.userAgent;
}
